import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme
} from '@mui/material';
import {
  AccountTree,
  Security,
  Cloud,
  Speed,
  CheckCircle
} from '@mui/icons-material';
import CommunityUsage from '../components/CommunityUsage';

const About = () => {
  const theme = useTheme();
  const features = [
    {
      icon: <AccountTree color="primary" />,
      title: 'Visual Diagrams',
      description: 'Generate beautiful Mermaid.js diagrams from your Intune policies and applications'
    },
    {
      icon: <Security color="primary" />,
      title: 'Secure Authentication',
      description: 'OAuth 2.0 integration with Microsoft Graph API for secure tenant access'
    },
    {
      icon: <Cloud color="primary" />,
      title: 'Cloud & Offline',
      description: 'Work with live tenant data or upload JSON files for offline diagram generation'
    },
    {
      icon: <Speed color="primary" />,
      title: 'Fast & Efficient',
      description: 'Optimized data processing for quick diagram generation'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* About IntuneMermaid Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            About IntuneMermaid
          </Typography>
          <Typography variant="body1" paragraph>
            IntuneMermaid is a web application designed to turn complex Microsoft Intune configurations 
            into clear, visual Mermaid diagrams. By automatically mapping policy and application assignments, 
            it transforms raw Intune data into intuitive, human-readable architecture diagrams.
          </Typography>
          
          <Typography variant="body1" paragraph>
            Built for administrators, engineers, and architects, IntuneMermaid simplifies the way you 
            understand your Intune environment. It helps you quickly spot configuration gaps, troubleshoot 
            assignment issues, and produce clean documentation with minimal effort.
          </Typography>
          
          <Typography variant="body1" paragraph>
            Powered by the Microsoft Graph API, the platform securely connects to your tenant, retrieves 
            policy and application relationships, and generates a complete visual representation of your 
            Intune landscape. Whether you're reviewing existing configurations, onboarding to a new tenant, 
            or documenting for audits and handovers, IntuneMermaid delivers a fast, visual way to understand 
            how everything fits together.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            What you can visualize:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="All your assigned Intune applications separated by operating system and resource type" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="All your assigned Intune configuration profiles separated by operating system and policy type" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Group Relationships" 
                secondary="You can see all resources (apps or policies) a specific group is assigned to."
              />
            </ListItem>
                        <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Specific application or policy" 
                secondary="Choose a specific application or policy from the selection box to see which groups it is assigned to."
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Sponsored By Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          py: 3,
          mb: 3,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            letterSpacing: 3,
            color: 'text.secondary',
            fontSize: '0.7rem',
            fontWeight: 500,
          }}
        >
          SPONSORED BY
        </Typography>
        <Link
          href="https://devicie.com/?utm_source=intunemermaid&utm_medium=referral"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textDecoration: 'none',
            opacity: 0.9,
            transition: 'all 0.2s',
            '&:hover': { 
              opacity: 1,
              transform: 'scale(1.02)',
            },
          }}
        >
          <Box
            component="img"
            src={theme.palette.mode === 'dark' ? '/devicie-light.png' : '/devicie-dark.png'}
            alt="Devicie"
            sx={{
              height: 55,
              width: 180,
              objectFit: 'contain',
            }}
          />
        </Link>
      </Box>

      {/* Community Usage Stats */}
      <CommunityUsage />

      {/* Features Section */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
        Key Features
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {features.map((feature, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                    {feature.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Sponsor Section */}
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Link
          href="https://github.com/sponsors/schenardie"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 3,
            py: 1.5,
            bgcolor: '#bf3989',
            color: '#ffffff',
            borderRadius: '6px',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            transition: 'background-color 0.2s',
            '&:hover': {
              bgcolor: '#9e2d72',
              textDecoration: 'none'
            }
          }}
        >
          <span role="img" aria-label="heart" style={{ fontSize: '1.1rem' }}>❤️</span>
          Sponsor
        </Link>
        
        <Box sx={{ mt: 3 }}>
          <Link
            component={RouterLink}
            to="/privacy"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'text.secondary',
              textDecoration: 'none',
              fontSize: '0.9rem',
              '&:hover': {
                color: 'primary.main',
                textDecoration: 'underline'
              }
            }}
          >
            <Security fontSize="small" />
            Privacy & Data Collection
          </Link>
        </Box>
      </Box>
    </Container>
  );
};

export default About;